import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CreditCard, Building2 } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
    account_number: string;
    company?: {
        id: string;
        name: string;
    };
}

interface PaymentInstrumentModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (instrumentId: string) => void;
    instruments: PaymentInstrument[];
    loading?: boolean;
}

export function PaymentInstrumentModal({
    open,
    onOpenChange,
    onSelect,
    instruments,
    loading = false
}: PaymentInstrumentModalProps) {
    const { t } = useI18n();
    const [selectedId, setSelectedId] = React.useState<string>('');

    // Reset selection when modal opens
    React.useEffect(() => {
        if (open && instruments.length > 0) {
            setSelectedId(instruments[0].id);
        }
    }, [open, instruments]);

    const handleConfirm = () => {
        if (selectedId) {
            onSelect(selectedId);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px] sm: max-h-[800px] overflow-auto">
                <DialogHeader>
                    <DialogTitle>{t('invoices.selectPaymentInstrument', 'finance') || 'Select Payment Instrument'}</DialogTitle>
                    <DialogDescription>
                        {t('invoices.selectPaymentInstrumentDesc', 'finance') || 'Choose which payment instrument was used for this payment.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                    <RadioGroup value={selectedId} onValueChange={setSelectedId} className="gap-4">
                        {instruments.map((instrument) => (
                            <div key={instrument.id} className="flex items-center space-x-2 border p-3 rounded-md hover:bg-muted/50 cursor-pointer" onClick={() => setSelectedId(instrument.id)}>
                                <RadioGroupItem value={instrument.id} id={instrument.id} />
                                <div className="flex-1 cursor-pointer">
                                    <Label htmlFor={instrument.id} className="cursor-pointer font-medium flex items-center gap-2">
                                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                                        {instrument.name}
                                    </Label>
                                    <div className="text-sm text-muted-foreground ml-6">
                                        {instrument.instrument_type}<br />
                                        {instrument.account_number}
                                        {instrument.company && (
                                            <span className="flex items-center gap-1 mt-1 text-xs">
                                                <Building2 className="h-3 w-3" />
                                                {instrument.company.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </RadioGroup>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        {t('actions.cancel', 'common')}
                    </Button>
                    <Button onClick={handleConfirm} disabled={!selectedId || loading}>
                        {loading ? t('actions.processing', 'common') : t('actions.confirm', 'common')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
